/** Поля анкеты «Данные о должности» — зеркало дочерних таблиц compensation_record_* */

export const SALARY_REVIEW_FREQUENCY_OPTIONS = [
  'Раз в год',
  'Два раза в год',
  'Ежеквартально',
  'Ежемесячно',
  'В индивидуальном порядке',
] as const;

export const QUARTERLY_SALARY_REVIEW_TIMING_OPTIONS = [
  'Последний месяц текущего квартала за текущий квартал',
  'Первый месяц следующего квартала за предыдущий',
  'Второй месяц следующего квартала за предыдущий',
  'Третий месяц следующего квартала за предыдущий',
] as const;

export const MONTH_OPTIONS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const;

export type SalaryReviewTimingKind = 'single' | 'double' | 'quarterly' | 'none';

export type StiSizingBasis = '' | 'kpi' | 'commission' | 'discretionary';

export type StiFormulaType = '' | 'additive' | 'multiplicative';

export const STI_SIZING_BASIS_OPTIONS: { value: StiSizingBasis; label: string }[] = [
  { value: 'kpi', label: 'Выполнение формализованных целей / КПЭ' },
  { value: 'commission', label: 'Комиссия от …' },
  { value: 'discretionary', label: 'Решение руководителя / discretionary bonus' },
];

export const STI_FORMULA_TYPE_OPTIONS: { value: StiFormulaType; label: string }[] = [
  { value: 'additive', label: 'Аддитивная (веса)' },
  { value: 'multiplicative', label: 'Мультипликативная (перемножение)' },
];

export type LtipProgramType =
  | ''
  | 'none'
  | 'real_carry'
  | 'phantom_carry'
  | 'equity_options'
  | 'phantom_equity'
  | 'other';

export type LtipVestingType = '' | 'linear' | 'cliff' | 'bullet' | 'event';

export type LtipValueMode = '' | 'percent' | 'absolute';

export const LTIP_PROGRAM_TYPE_OPTIONS: { value: LtipProgramType; label: string }[] = [
  { value: 'none', label: 'Не предоставляется' },
  {
    value: 'real_carry',
    label: 'Реальный Carried Interest (доля в прибыли через юридически закрепленное совладение)',
  },
  {
    value: 'phantom_carry',
    label: 'Виртуальный / Фантомный Carry (денежное вознаграждение без юридически закрепленного участия/совладения)',
  },
  { value: 'equity_options', label: 'Акции / опционы Компании' },
  { value: 'phantom_equity', label: 'Фантомные акции / опционы Компании (деньги)' },
  { value: 'other', label: 'Другое' },
];

export const LTIP_DURATION_PRESET_OPTIONS = [
  '3 года',
  '4 года',
  '5 лет',
  'более 5 лет',
] as const;

export const LTIP_DURATION_OTHER = 'Другое';

export const LTIP_DURATION_OPTIONS = [
  ...LTIP_DURATION_PRESET_OPTIONS,
  LTIP_DURATION_OTHER,
] as const;

function isLtipDurationPreset(value: string): boolean {
  return (LTIP_DURATION_PRESET_OPTIONS as readonly string[]).includes(value);
}

export function resolveLtipProgramDurationForStorage(data: PositionFormData): string {
  if (data.ltipProgramDuration === LTIP_DURATION_OTHER) {
    return data.ltipProgramDurationOther.trim();
  }
  return data.ltipProgramDuration.trim();
}

function splitLtipProgramDurationForUi(stored: string): { selection: string; other: string } {
  const value = stored.trim();
  if (!value) return { selection: '', other: '' };
  if (value === LTIP_DURATION_OTHER || isLtipDurationPreset(value)) {
    return { selection: value, other: '' };
  }
  return { selection: LTIP_DURATION_OTHER, other: value };
}

export const LTIP_VESTING_OPTIONS: { value: LtipVestingType; label: string }[] = [
  {
    value: 'linear',
    label: 'Равномерно (Linear): Права начисляются равными частями каждый год (например, по 25% в год)',
  },
  {
    value: 'cliff',
    label: 'С порогом (Cliff): Первый год не начисляется ничего, затем сразу 25% или 33%, далее линейно',
  },
  {
    value: 'bullet',
    label: 'В конце срока (Bullet / К концу программы): Все 100% прав переходят только в финале программы',
  },
  { value: 'event', label: 'По наступлению определенных событий (event based)' },
];

const LTIP_PROGRAM_VALUE_LINE_PREFIX = 'Стоимость программы: ';

export const LTIP_VALUE_MODE_OPTIONS: { value: LtipValueMode; label: string }[] = [
  { value: 'percent', label: '% от годового базового оклада' },
  { value: 'absolute', label: 'Абсолютное значение, ₽ gross' },
];

export function salaryReviewTimingKind(frequency: string): SalaryReviewTimingKind {
  switch (frequency) {
    case 'Раз в год':
      return 'single';
    case 'Два раза в год':
      return 'double';
    case 'Ежеквартально':
      return 'quarterly';
    default:
      return 'none';
  }
}

export function payoutTimingKind(frequency: string): SalaryReviewTimingKind {
  return salaryReviewTimingKind(frequency);
}

export type PositionFormData = {
  industryName: string;
  sectorName: string;
  companyName: string;
  companyRoleName: string;
  /** Фиксированная оплата в месяц (gross), ввод пользователя */
  baseGrossSalaryMonthly: string;
  /** Годовой base (gross) — для compensation_record_base.base_gross_salary */
  baseGrossSalary: string;
  salaryReviewFrequency: string;
  salaryReviewDate: string;
  salaryReviewDate2: string;
  salaryReviewCriteria: string;
  stiTargetPct: string;
  stiPayoutFrequency: string;
  stiPayoutMonth: string;
  stiPayoutMonth2: string;
  /** UI: от чего зависит размер премии */
  stiSizingBasis: StiSizingBasis;
  stiSizingCriteria: string;
  stiCorporateKpiExamples: string;
  stiIndividualKpiExamples: string;
  /** UI: комиссия — ключевое событие */
  stiCommissionEvent: string;
  /** UI: комиссия — % или диапазон */
  stiCommissionPct: string;
  /** UI: аддитивная / мультипликативная */
  stiFormulaType: StiFormulaType;
  stiFormula: string;
  stiCorporateKpiWeight: string;
  stiIndividualKpiWeight: string;
  /** UI: тип программы LTIP */
  ltipProgramType: LtipProgramType;
  /** UI: описание при выборе «Другое» */
  ltipFormOther: string;
  /** UI: стоимость всей программы LTI */
  ltipProgramValueMode: LtipValueMode;
  ltipProgramValuePct: string;
  ltipProgramValueAmount: string;
  /** UI: вестинг */
  ltipVestingType: LtipVestingType;
  /** UI: выплата за последний год */
  ltipLastPayoutMode: LtipValueMode;
  ltipLastPayoutPct: string;
  ltipLastPayoutAmount: string;
  ltipForm: string;
  ltipAmount: string;
  ltipProgramDuration: string;
  /** UI: длительность LTI при выборе «Другое» */
  ltipProgramDurationOther: string;
  ltipVestingSchedule: string;
  geography: string;
  investmentStagePriority: string;
  aumData: string;
  lifetimeCycle: string;
  /** Заполняется автоматически при отправке анкеты */
  actualDate: string;
  /** Заполняется автоматически: candidate */
  source: string;
  comment: string;
  /** @deprecated миграция из старой анкеты */
  fix?: string;
  /** @deprecated */
  sti?: string;
  /** @deprecated */
  ltip?: boolean;
  /** @deprecated */
  ltipTarget?: string;
  /** @deprecated */
  totalCompTarget?: string;
};

export function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export const POSITION_QUESTIONNAIRE_SOURCE = 'candidate';

export function emptyPositionForm(): PositionFormData {
  return {
    industryName: '',
    sectorName: '',
    companyName: '',
    companyRoleName: '',
    baseGrossSalaryMonthly: '',
    baseGrossSalary: '',
    salaryReviewFrequency: '',
    salaryReviewDate: '',
    salaryReviewDate2: '',
    salaryReviewCriteria: '',
    stiTargetPct: '',
    stiPayoutFrequency: '',
    stiPayoutMonth: '',
    stiPayoutMonth2: '',
    stiSizingBasis: '',
    stiSizingCriteria: '',
    stiCorporateKpiExamples: '',
    stiIndividualKpiExamples: '',
    stiCommissionEvent: '',
    stiCommissionPct: '',
    stiFormulaType: '',
    stiFormula: '',
    stiCorporateKpiWeight: '',
    stiIndividualKpiWeight: '',
    ltipProgramType: '',
    ltipFormOther: '',
    ltipProgramValueMode: '',
    ltipProgramValuePct: '',
    ltipProgramValueAmount: '',
    ltipVestingType: '',
    ltipLastPayoutMode: '',
    ltipLastPayoutPct: '',
    ltipLastPayoutAmount: '',
    ltipForm: '',
    ltipAmount: '',
    ltipProgramDuration: '',
    ltipProgramDurationOther: '',
    ltipVestingSchedule: '',
    geography: '',
    investmentStagePriority: '',
    aumData: '',
    lifetimeCycle: '',
    actualDate: '',
    source: POSITION_QUESTIONNAIRE_SOURCE,
    comment: '',
  };
}

export type PositionProfilePrefillSource = {
  sectorName: string;
  companyName: string;
  positionName: string;
};

export type MarketReferenceNames = {
  industries: { id: number; name: string }[];
  sectors: { id: number; industryId: number; name: string }[];
};

export function buildPositionPrefillSource(
  profile: { sectorName?: string; companyName?: string; positionName?: string },
  application?: { sector?: string | null; company?: string | null; title?: string | null } | null,
): PositionProfilePrefillSource {
  return {
    sectorName: profile.sectorName?.trim() || application?.sector?.trim() || '',
    companyName: profile.companyName?.trim() || application?.company?.trim() || '',
    positionName: profile.positionName?.trim() || application?.title?.trim() || '',
  };
}

export function resolveIndustryNameBySector(
  sectorName: string,
  ref: MarketReferenceNames,
): string {
  const normalized = sectorName.trim().toLowerCase();
  if (!normalized) return '';
  const sector = ref.sectors.find((s) => s.name.trim().toLowerCase() === normalized);
  if (!sector) return '';
  return ref.industries.find((i) => i.id === sector.industryId)?.name?.trim() ?? '';
}

/** Подставляет пустые поля должности из профиля / заявки на доступ (не перезаписывает уже введённое). */
export function prefillPositionFromProfileSource(
  position: PositionFormData,
  source: PositionProfilePrefillSource,
  ref?: MarketReferenceNames | null,
): PositionFormData {
  const sectorName = position.sectorName.trim() || source.sectorName.trim();
  const industryFromRef = sectorName && ref ? resolveIndustryNameBySector(sectorName, ref) : '';
  return normalizePositionFormData({
    ...position,
    industryName: position.industryName.trim() || industryFromRef,
    sectorName,
    companyName: position.companyName.trim() || source.companyName.trim(),
    companyRoleName: position.companyRoleName.trim() || source.positionName.trim(),
  });
}

/** Синхронизирует годовой base из месячного ввода (×12) для хранения в compensation_record_base */
export function syncAnnualBaseFromMonthly(data: PositionFormData): PositionFormData {
  const monthly = parseMoney(data.baseGrossSalaryMonthly);
  if (monthly == null || monthly <= 0) {
    return { ...data, baseGrossSalary: '' };
  }
  return { ...data, baseGrossSalary: String(Math.round(monthly * 12)) };
}

/** Синхронизирует поля STI для хранения в compensation_record_sti */
export function syncStiFieldsForStorage(data: PositionFormData): PositionFormData {
  const next = { ...data };
  const basisLabel = STI_SIZING_BASIS_OPTIONS.find((o) => o.value === data.stiSizingBasis)?.label ?? '';

  if (data.stiSizingBasis === 'kpi') {
    next.stiSizingCriteria = basisLabel;
    next.stiCommissionEvent = '';
    next.stiCommissionPct = '';
  } else if (data.stiSizingBasis === 'commission') {
    next.stiSizingCriteria = basisLabel;
    next.stiCorporateKpiExamples = data.stiCommissionEvent.trim();
    next.stiIndividualKpiExamples = data.stiCommissionPct.trim()
      ? `Процент комиссии: ${data.stiCommissionPct.trim()}`
      : '';
    next.stiFormulaType = '';
    next.stiFormula = '';
    next.stiCorporateKpiWeight = '';
    next.stiIndividualKpiWeight = '';
  } else if (data.stiSizingBasis === 'discretionary') {
    next.stiSizingCriteria = basisLabel;
    next.stiCorporateKpiExamples = '';
    next.stiIndividualKpiExamples = '';
    next.stiCommissionEvent = '';
    next.stiCommissionPct = '';
    next.stiFormulaType = '';
    next.stiFormula = '';
    next.stiCorporateKpiWeight = '';
    next.stiIndividualKpiWeight = '';
  }

  if (data.stiFormulaType === 'additive') {
    next.stiFormula = 'Аддитивная (веса)';
  } else if (data.stiFormulaType === 'multiplicative') {
    next.stiFormula = 'Мультипликативная (перемножение)';
    next.stiCorporateKpiWeight = '';
    next.stiIndividualKpiWeight = '';
  }

  return next;
}

function ltipProgramTypeLabel(type: LtipProgramType, otherText: string): string {
  if (type === 'other') {
    const custom = otherText.trim();
    return custom ? `Другое: ${custom}` : 'Другое';
  }
  return LTIP_PROGRAM_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? '';
}

function formatLtipProgramValueForStorage(mode: LtipValueMode, pct: string, amount: string): string {
  if (mode === 'percent' && pct.trim()) {
    return `${LTIP_PROGRAM_VALUE_LINE_PREFIX}${pct.trim()}% от base`;
  }
  if (mode === 'absolute' && amount.trim()) {
    return `${LTIP_PROGRAM_VALUE_LINE_PREFIX}${amount.trim()} ₽ gross`;
  }
  return '';
}

function parseLtipProgramValueLine(line: string): {
  mode: LtipValueMode;
  pct: string;
  amount: string;
} {
  const trimmed = line.trim();
  if (!trimmed.startsWith(LTIP_PROGRAM_VALUE_LINE_PREFIX)) {
    return { mode: '', pct: '', amount: '' };
  }
  const value = trimmed.slice(LTIP_PROGRAM_VALUE_LINE_PREFIX.length).trim();
  if (value.includes('% от base')) {
    return {
      mode: 'percent',
      pct: value.replace('% от base', '').trim(),
      amount: '',
    };
  }
  if (value.includes('₽')) {
    return {
      mode: 'absolute',
      pct: '',
      amount: value.replace(/₽\s*gross/i, '').trim(),
    };
  }
  return { mode: '', pct: '', amount: '' };
}

export function resolveLtipRublesFromMode(
  mode: LtipValueMode,
  pct: string,
  amount: string,
  annualBase: string,
): number | null {
  if (mode === 'percent') {
    const p = parsePercentInput(pct);
    const base = parseMoney(annualBase);
    if (p == null || base == null || base <= 0) return null;
    return Math.round((base * p) / 100);
  }
  if (mode === 'absolute') {
    const rub = parseMoney(amount);
    return rub != null && rub >= 0 ? Math.round(rub) : null;
  }
  return null;
}

/** Синхронизирует поля LTIP для хранения в compensation_record_ltip */
export function syncLtipFieldsForStorage(data: PositionFormData): PositionFormData {
  const next = { ...data };

  if (data.ltipProgramType === 'none' || !data.ltipProgramType) {
    next.ltipForm = '';
    next.ltipAmount = '';
    next.ltipProgramDuration = '';
    next.ltipProgramDurationOther = '';
    next.ltipVestingSchedule = '';
    return next;
  }

  const typeLabel = ltipProgramTypeLabel(data.ltipProgramType, data.ltipFormOther);
  const programValueLine = formatLtipProgramValueForStorage(
    data.ltipProgramValueMode,
    data.ltipProgramValuePct,
    data.ltipProgramValueAmount,
  );
  next.ltipForm = programValueLine ? `${typeLabel}\n${programValueLine}` : typeLabel;
  next.ltipProgramDuration = resolveLtipProgramDurationForStorage(data);
  next.ltipVestingSchedule = LTIP_VESTING_OPTIONS.find((o) => o.value === data.ltipVestingType)?.label ?? '';

  const synced = syncAnnualBaseFromMonthly(data);
  const lastPayoutRub = resolveLtipRublesFromMode(
    data.ltipLastPayoutMode,
    data.ltipLastPayoutPct,
    data.ltipLastPayoutAmount,
    synced.baseGrossSalary,
  );
  next.ltipAmount = lastPayoutRub != null ? String(lastPayoutRub) : '';

  return next;
}

export function syncCompensationFieldsForStorage(data: PositionFormData): PositionFormData {
  return syncLtipFieldsForStorage(syncStiFieldsForStorage(syncAnnualBaseFromMonthly(data)));
}

/** При отправке анкеты: дата = сегодня, источник = candidate, без примечания, годовой base из месячного */
export function finalizePositionFormForSubmit(data: PositionFormData): PositionFormData {
  return normalizePositionFormData(syncCompensationFieldsForStorage({
    ...data,
    actualDate: todayIsoDate(),
    source: POSITION_QUESTIONNAIRE_SOURCE,
    comment: '',
  }));
}

/** Поддержка черновиков, сохранённых до разбиения на страницы */
export function normalizePositionFormData(raw: Partial<PositionFormData> | null | undefined): PositionFormData {
  const base = { ...emptyPositionForm(), ...(raw ?? {}) };
  if (!base.baseGrossSalaryMonthly.trim() && base.baseGrossSalary.trim()) {
    const annual = parseMoney(base.baseGrossSalary);
    if (annual != null && annual > 0) {
      base.baseGrossSalaryMonthly = String(Math.round(annual / 12));
    }
  }
  if (!base.baseGrossSalary.trim() && base.fix?.trim()) {
    const legacy = parseMoney(base.fix);
    if (legacy != null && legacy > 0) {
      base.baseGrossSalary = base.fix.trim();
      if (!base.baseGrossSalaryMonthly.trim()) {
        base.baseGrossSalaryMonthly = String(Math.round(legacy / 12));
      }
    }
  }
  if (!base.stiTargetPct.trim() && base.sti?.trim()) {
    base.stiTargetPct = base.sti.trim();
  }
  if (!base.stiSizingBasis && base.stiSizingCriteria.trim()) {
    const criteria = base.stiSizingCriteria.toLowerCase();
    if (criteria.includes('кпэ') || criteria.includes('формализован')) {
      base.stiSizingBasis = 'kpi';
    } else if (criteria.includes('комиссия')) {
      base.stiSizingBasis = 'commission';
    } else if (criteria.includes('руководител') || criteria.includes('discretionary')) {
      base.stiSizingBasis = 'discretionary';
    }
  }
  if (!base.stiFormulaType && base.stiFormula.trim()) {
    if (base.stiFormula.toLowerCase().includes('аддитив')) {
      base.stiFormulaType = 'additive';
    } else if (base.stiFormula.toLowerCase().includes('мультип') || base.stiFormula.includes('перемнож')) {
      base.stiFormulaType = 'multiplicative';
    }
  }
  if (base.stiSizingBasis === 'commission' && !base.stiCommissionEvent && base.stiCorporateKpiExamples.trim()) {
    base.stiCommissionEvent = base.stiCorporateKpiExamples.trim();
  }
  if (base.stiSizingBasis === 'commission' && !base.stiCommissionPct && base.stiIndividualKpiExamples.trim()) {
    base.stiCommissionPct = base.stiIndividualKpiExamples.replace(/^Процент комиссии:\s*/i, '').trim();
  }
  if (!base.ltipAmount.trim() && base.ltipTarget?.trim()) {
    base.ltipAmount = base.ltipTarget.trim();
  }
  if (!base.ltipProgramType) {
    if (base.ltipForm.trim()) {
      const [firstLine, ...rest] = base.ltipForm.split('\n');
      const formLower = firstLine.toLowerCase();
      if (formLower.includes('не предоставля')) {
        base.ltipProgramType = 'none';
      } else if (formLower.startsWith('другое')) {
        base.ltipProgramType = 'other';
        base.ltipFormOther = firstLine.replace(/^другое:?\s*/i, '').trim();
      } else if (formLower.includes('реальный') && formLower.includes('carried')) {
        base.ltipProgramType = 'real_carry';
      } else if (formLower.includes('фантомный carry') || formLower.includes('виртуальный')) {
        base.ltipProgramType = 'phantom_carry';
      } else if (formLower.includes('фантомные акции')) {
        base.ltipProgramType = 'phantom_equity';
      } else if (formLower.includes('акции') || formLower.includes('опционы компании')) {
        base.ltipProgramType = 'equity_options';
      } else if (firstLine.trim()) {
        base.ltipProgramType = 'other';
        base.ltipFormOther = firstLine.trim();
      }
      const programValueLine = rest.find((line) => line.trim().startsWith(LTIP_PROGRAM_VALUE_LINE_PREFIX));
      if (programValueLine) {
        const parsed = parseLtipProgramValueLine(programValueLine);
        base.ltipProgramValueMode = parsed.mode;
        base.ltipProgramValuePct = parsed.pct;
        base.ltipProgramValueAmount = parsed.amount;
      }
    } else if (base.ltip) {
      base.ltipProgramType = 'other';
      base.ltipFormOther = 'LTIP';
    }
  }
  if (!base.ltipVestingType && base.ltipVestingSchedule.trim()) {
    const vestingLower = base.ltipVestingSchedule.toLowerCase();
    if (vestingLower.includes('linear') || vestingLower.includes('равномерно')) {
      base.ltipVestingType = 'linear';
    } else if (vestingLower.includes('cliff') || vestingLower.includes('порогом')) {
      base.ltipVestingType = 'cliff';
    } else if (vestingLower.includes('bullet') || vestingLower.includes('конце срока')) {
      base.ltipVestingType = 'bullet';
    } else if (vestingLower.includes('event') || vestingLower.includes('событи')) {
      base.ltipVestingType = 'event';
    }
  }
  if (!base.ltipLastPayoutMode && base.ltipAmount.trim()) {
    base.ltipLastPayoutMode = 'absolute';
    base.ltipLastPayoutAmount = base.ltipAmount.trim();
  }
  if (base.ltipProgramDuration.trim()) {
    const durationUi = splitLtipProgramDurationForUi(base.ltipProgramDuration);
    base.ltipProgramDuration = durationUi.selection;
    if (durationUi.other) {
      base.ltipProgramDurationOther = durationUi.other;
    }
  }
  if (!base.ltipForm.trim() && base.ltip) {
    base.ltipForm = 'LTIP';
  }
  if (!base.source?.trim()) {
    base.source = POSITION_QUESTIONNAIRE_SOURCE;
  }
  return syncAnnualBaseFromMonthly(base);
}

export function positionFixMonthlyValue(data: PositionFormData): string {
  return data.baseGrossSalaryMonthly.trim()
    || (() => {
      const annual = parseMoney(positionFixValue(data));
      return annual != null && annual > 0 ? String(Math.round(annual / 12)) : '';
    })();
}

export function validateBaseSalaryStep(form: PositionFormData): string | null {
  const monthly = parseMoney(form.baseGrossSalaryMonthly);
  if (monthly == null || monthly <= 0) {
    return 'Укажите корректный размер фиксированной оплаты в месяц';
  }
  if (!form.salaryReviewFrequency.trim()) {
    return 'Выберите, как часто компания пересматривает заработную плату';
  }

  const timing = salaryReviewTimingKind(form.salaryReviewFrequency);
  if (timing === 'single' && !form.salaryReviewDate.trim()) {
    return 'Выберите месяц пересмотра';
  }
  if (timing === 'double') {
    if (!form.salaryReviewDate.trim() || !form.salaryReviewDate2.trim()) {
      return 'Выберите оба месяца пересмотра';
    }
    if (form.salaryReviewDate === form.salaryReviewDate2) {
      return 'Месяцы пересмотра должны различаться';
    }
  }
  if (timing === 'quarterly' && !form.salaryReviewDate.trim()) {
    return 'Выберите вариант пересмотра';
  }
  return null;
}

export function parsePercentInput(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value.replace(/\s/g, '').replace(',', '.').replace('%', ''));
  return Number.isNaN(n) ? null : n;
}

export function validateStiStep(form: PositionFormData): string | null {
  const targetPct = parsePercentInput(form.stiTargetPct);
  if (targetPct == null || targetPct < 0) {
    return 'Укажите целевой размер STI в % (0 — если премии нет)';
  }
  if (targetPct === 0) return null;

  if (!form.stiPayoutFrequency.trim()) {
    return 'Выберите, как часто выплачивается премия';
  }

  const timing = payoutTimingKind(form.stiPayoutFrequency);
  if (timing === 'single' && !form.stiPayoutMonth.trim()) {
    return 'Выберите месяц выплаты премии';
  }
  if (timing === 'double') {
    if (!form.stiPayoutMonth.trim() || !form.stiPayoutMonth2.trim()) {
      return 'Выберите оба месяца выплаты премии';
    }
    if (form.stiPayoutMonth === form.stiPayoutMonth2) {
      return 'Месяцы выплаты должны различаться';
    }
  }
  if (timing === 'quarterly' && !form.stiPayoutMonth.trim()) {
    return 'Выберите вариант выплаты премии';
  }

  if (!form.stiSizingBasis) {
    return 'Выберите, от чего зависит размер премии';
  }

  if (form.stiSizingBasis === 'kpi') {
    if (!form.stiCorporateKpiExamples.trim()) {
      return 'Укажите корпоративные показатели, влияющие на премию';
    }
    if (!form.stiIndividualKpiExamples.trim()) {
      return 'Укажите показатели вашей роли, влияющие на премию';
    }
    if (!form.stiFormulaType) {
      return 'Выберите тип формулы расчёта премии';
    }
    if (form.stiFormulaType === 'additive') {
      const corp = parsePercentInput(form.stiCorporateKpiWeight);
      const ind = parsePercentInput(form.stiIndividualKpiWeight);
      if (corp == null || corp < 0) return 'Укажите вес корпоративных показателей в %';
      if (ind == null || ind < 0) return 'Укажите вес индивидуальных показателей в %';
    }
  }

  if (form.stiSizingBasis === 'commission') {
    if (!form.stiCommissionEvent.trim()) {
      return 'Укажите ключевое событие, к которому привязана комиссия';
    }
    if (!form.stiCommissionPct.trim()) {
      return 'Укажите процент комиссии или диапазон';
    }
  }

  return null;
}

function validateLtipValueInput(
  mode: LtipValueMode,
  pct: string,
  amount: string,
  fieldLabel: string,
): string | null {
  if (!mode) {
    return `Выберите способ указания: ${fieldLabel}`;
  }
  if (mode === 'percent') {
    const p = parsePercentInput(pct);
    if (p == null || p < 0) {
      return `Укажите ${fieldLabel} в % от годового базового оклада`;
    }
    return null;
  }
  const rub = parseMoney(amount);
  if (rub == null || rub < 0) {
    return `Укажите ${fieldLabel} в рублях gross`;
  }
  return null;
}

export function validateLtipStep(form: PositionFormData): string | null {
  if (!form.ltipProgramType) {
    return 'Укажите, участвуете ли вы в LTIP и к какому типу относится программа';
  }
  if (form.ltipProgramType === 'none') {
    return null;
  }
  if (form.ltipProgramType === 'other' && !form.ltipFormOther.trim()) {
    return 'Опишите тип программы LTIP';
  }

  const programValueError = validateLtipValueInput(
    form.ltipProgramValueMode,
    form.ltipProgramValuePct,
    form.ltipProgramValueAmount,
    'совокупную стоимость программы LTI',
  );
  if (programValueError) return programValueError;

  if (!form.ltipProgramDuration.trim()) {
    return 'Выберите общую длительность программы LTI';
  }
  if (form.ltipProgramDuration === LTIP_DURATION_OTHER && !form.ltipProgramDurationOther.trim()) {
    return 'Укажите общую длительность программы LTI';
  }
  if (!form.ltipVestingType) {
    return 'Выберите схему вестинга';
  }

  const payoutError = validateLtipValueInput(
    form.ltipLastPayoutMode,
    form.ltipLastPayoutPct,
    form.ltipLastPayoutAmount,
    'выплату по программе LTI за последний период',
  );
  if (payoutError) return payoutError;

  return null;
}

export function positionFixValue(data: PositionFormData): string {
  return data.baseGrossSalary.trim() || data.fix?.trim() || '';
}

export function positionStiValue(data: PositionFormData): string {
  return data.stiTargetPct.trim() || data.sti?.trim() || '';
}

export function positionLtipAmountValue(data: PositionFormData): string {
  return data.ltipAmount.trim() || data.ltipTarget?.trim() || '';
}

export function positionHasLtip(data: PositionFormData): boolean {
  if (data.ltipProgramType) {
    return data.ltipProgramType !== 'none';
  }
  return Boolean(
    data.ltipForm.trim()
    || data.ltipAmount.trim()
    || data.ltipProgramDuration.trim()
    || data.ltipVestingSchedule.trim()
    || data.ltip
    || data.ltipTarget?.trim(),
  );
}

export function parseMoney(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value.replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

export function formatRub(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value.replace(/\s/g, '')) : value;
  if (n == null || Number.isNaN(n) || n === 0) return '—';
  return `${new Intl.NumberFormat('ru-RU').format(n)} ₽`;
}

export function formatPercentValue(value: string): string {
  const trimmed = value.trim();
  return trimmed ? `${trimmed}%` : '—';
}

/** Расчётный Total Comp: base + STI% + LTIP amount */
export function computeTotalComp(data: PositionFormData): number | null {
  const fix = parseMoney(positionFixValue(data));
  if (fix == null) return null;
  const stiPct = parseMoney(positionStiValue(data)) ?? 0;
  const ltip = positionHasLtip(data) ? parseMoney(positionLtipAmountValue(data)) ?? 0 : 0;
  const manual = parseMoney(data.totalCompTarget ?? '');
  if (manual != null && manual > 0) return manual;
  return Math.round(fix + (fix * stiPct) / 100 + ltip);
}
